import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthHTTPService } from './auth-http';
import { ResponseModel } from '../_models/response.model';
import { UserRegisterModel } from '../_models/user-register.model';
import { SecretWordModel } from '@modules/auth/_models/secret-word.model';
import { AppStateService } from '../../../store/app-state.service';
import { LANDBOT_TOKEN, TWILIO_USER_TOKEN_KEY, USER_EMAIL, USER_TOKEN_KEY } from '@shared/constants';
import { MessengerService } from '@modules/messenger/messenger.service';
import { UserDataModel } from '@modules/auth';
import { environment } from 'src/environments/environment';
import { PendoService } from '../../../_services/pendo.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  currentUser$: Observable<UserDataModel> = this.appStateService.currentUser$;

  constructor(
    private authHttpService: AuthHTTPService,
    private appStateService: AppStateService,
    private messengerService: MessengerService,
    private pendoService: PendoService,
  ) {
  }

  get currentUserValue(): UserDataModel {
    return this.appStateService.state.currentUser;
  }

  set currentUserValue(user: UserDataModel) {
    this.appStateService.patchState({currentUser: user});
  }

  login(login: string, password: string): Observable<ResponseModel<UserDataModel> | undefined> {
    return this.authorizeUser({login, password});
  }

  logout() {
    localStorage.removeItem(USER_TOKEN_KEY);
    localStorage.removeItem(TWILIO_USER_TOKEN_KEY);
    // TODO reset all state
    // this.appStateService.reset();
    // this.usersCollectionService.clearCache();
    // this.messagesCollectionService.clearCache();
    // this.participantsCollectionService.clearCache();
    // this.conversationsCollectionService.clearCache();
    
    // Reset Pendo to anonymous user
    this.pendoService.logout();
    
    this.messengerService.shutDown()
      .finally(() => window.location.pathname = '/auth');
  }

  getUserByToken(): Observable<ResponseModel<UserDataModel> | undefined> {
    const persistentJWT = localStorage.getItem(USER_TOKEN_KEY);
    return persistentJWT ? this.authorizeUser(persistentJWT) : of(undefined);
  }

  authorizeUser(credentials: {
    login: string,
    password: string
  } | string /*token*/): Observable<ResponseModel<UserDataModel> | undefined> {
    return !credentials ? of(undefined) : (typeof credentials === 'string'
        ? this.authHttpService.getUserByToken(credentials)
        : this.authHttpService.login(credentials.login, credentials.password)
    ).pipe(
      map(respUser => {
        if (respUser.succeeded) {
          const user = respUser.data;
          localStorage.setItem(USER_TOKEN_KEY, user.jwToken);
          this.appStateService.patchState({currentUser: user});
          this.messengerService.getTwilioToken(user.id).subscribe(); // Do not wait for Twilio here
          const prevUser = localStorage.getItem(USER_EMAIL);
          if (prevUser !== user.email) {
            // Reset LandBot token if another user logged in
            localStorage.removeItem(`${LANDBOT_TOKEN}/${environment.landbotId}`);
            localStorage.setItem(USER_EMAIL, user.email);
          }
          
          // Initialize Pendo with user data
          this.pendoService.identifyUser(user);
        }
        return respUser;
      }),
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
    );
  }

  registration(user: UserRegisterModel): Observable<ResponseModel<UserDataModel>> {
    return this.authHttpService.createUser(user).pipe(
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    return this.authHttpService.forgotPassword(email);
  }

  update(word: string): Observable<ResponseModel<number>> {
    return this.authHttpService.updateSecretWord(word).pipe(
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
    );
  }

  getSecretWord(): Observable<ResponseModel<SecretWordModel>> {
    return this.authHttpService.getSecretWord().pipe(
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
    );
  }

  resetPassword(email: string, token: string, password: string, cPassword: string): Observable<ResponseModel<string>> {
    return this.authHttpService.resetPassword(email, token, password, cPassword).pipe(
      catchError(err => {
        console.error('err', err);
        return of(undefined);
      }),
    );
  }

  confirmEmail(param: { code: any; userId: any }) {
    return this.authHttpService.confirmEmail(param);
  }
}
